import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import ImageCropper from '../../components/misc/ImageCropper';
import Guid from '../components/Guid';
import ImageSlot from '../components/ImageSlot';
import LookupSelect from '../components/LookupSelect';
import RelatedLinks from '../components/RelatedLinks';
import { lookupProvider } from '../lookups';
import { languageLabel } from '../../components/pages/seoOptions';

/**
 * Facility — a venue: where it is, what it looks like, who runs it.
 *
 * The biggest page in the old admin (900 lines) and the one with the most going
 * on: nine fields, two lookups, a locker, and four pictures each with an upload,
 * a crop step and — for three of them — a generated preview.
 *
 * All of it is carried. The **crop step especially**: each slot has its own
 * aspect ratio (1:1 for the logo and the square photo, 240×144 for the list
 * banner, 1015×425 for the huge one) and those ratios are what make the pictures
 * usable on the site. Uploading without cropping would put a stretched banner on
 * a venue page and nobody would notice until it was live. The cropper itself is
 * the old admin's — imported, not copied, because it is behaviour rather than
 * appearance and rewriting `react-easy-crop` glue would risk exactly the bug the
 * step exists to prevent.
 *
 * Same single-mode form as the provider page: no Edit button, Save disabled until
 * something changed, Discard restores what the server sent.
 *
 * One thing worth knowing before reading `editFacilityData`: it fills in a
 * surprising amount by itself — an empty contact, `Type: 'Undefined'`, `Category:
 * 'None'`, `PlaceArea: 'some area'`, and a hardcoded fallback image key when the
 * main photo is empty. This page sends what the old form sent and lets the api
 * module keep doing that; changing it is a backend conversation, not a rewrite.
 * The one default that did real damage is gone: it used to send `Description: ''`
 * too, which wiped the facility text on every save. That text now lives in its own
 * table, edited by the Content & SEO section below the form.
 */

// A coordinate the facility cannot do without: present, a number, in range.
const coordinateError = (value, limit) => {
  const text = String(value ?? '').trim();
  if (text === '') return 'Required: a facility needs its position.';
  const number = Number(text);
  if (!Number.isFinite(number)) return 'Not a number.';
  return Math.abs(number) > limit ? `Between -${limit} and ${limit}.` : null;
};

const RATIOS = {
  logo: 1,
  square: 1,
  list: 240 / 144,
  huge: 1015 / 425
};

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const FacilityInternal = ({ api }) => {
  const { facilityId } = useParams();

  const [facility, setFacility] = useState(null);
  const [form, setForm] = useState(null);
  const [lockers, setLockers] = useState([]);
  const [facilityTexts, setFacilityTexts] = useState([]);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  // The crop step, when a file has been chosen and not yet cropped.
  const [cropping, setCropping] = useState(null);

  const toForm = data => ({
    Id: data.Id,
    CreatedAt: data.CreatedAt,
    Name: data.Name || '',
    Slug: data.Slug || '',
    PlaceAddress: data.PlaceAddress?.String || '',
    Latitude: data.PlaceAddress?.Location?.Latitude ?? '',
    Longitude: data.PlaceAddress?.Location?.Longitude ?? '',
    PlaceEmailAddress: data.PlaceEmailAddress?.Email || '',
    PlacePhoneNumber: data.PlacePhoneNumber?.Number || '',
    PlaceWebsiteUrl: data.PlaceWebsiteUrl || '',
    PicturePreview: data.PicturePreview || '',
    PlaceLogoUrl: data.PlaceLogoUrl || '',
    PlacePhotoUrl: data.PlacePhotoUrl || '',
    PlacePhotoSquareUrl: data.PlacePhotoSquareUrl || '',
    PlacePhotoSquarePreviewUrl: data.PlacePhotoSquarePreviewUrl || '',
    PlacePhotoListUrl: data.PlacePhotoListUrl || '',
    PlacePhotoListPreviewUrl: data.PlacePhotoListPreviewUrl || '',
    PlacePhotoHugeUrl: data.PlacePhotoHugeUrl || '',
    PlacePhotoHugePreviewUrl: data.PlacePhotoHugePreviewUrl || '',
    LockerId: data.LockerId || '',
    ProviderId: data.ProviderId || '',
    ProviderName: data.ProviderName || '',
    AreaId: data.Area?.AreaId || '',
    AreaName: data.Area?.Name || ''
  });

  const load = useCallback(async () => {
    try {
      const data = await api.facilities.getFacility(facilityId);
      if (!data || data.success === false || !data.Id) {
        setFailed(true);
        return;
      }
      setFacility(data);
      setForm(toForm(data));
      setFailed(false);
    } catch (error) {
      setFailed(true);
      return;
    }

    // The editorial texts of this facility. Loaded apart from the facility itself so a failure
    // here leaves the screen working, and so the card can refresh on its own.
    try {
      const texts = await api.facilityContent.searchFacilityTexts({ filters: { facilityId } });
      setFacilityTexts(Array.isArray(texts) ? texts : []);
    } catch (error) {
      setFacilityTexts([]);
    }

    try {
      const answer = await api.lockers.getLockersByFacilityId(facilityId);
      // The endpoint is named for one locker and the api module wraps whatever it
      // answers in `{ success, data }`, so `data` is an array on some facilities
      // and a single locker on others. Both become a list here.
      const payload = answer?.success ? answer.data : null;
      const list = Array.isArray(payload)
        ? payload
        : (payload && (payload.Id || payload.id) ? [payload] : []);
      setLockers(list);
    } catch (error) {
      // No lockers is a normal answer; an unreachable endpoint reads the same
      // here, and the select simply has nothing to offer.
      setLockers([]);
    }
  }, [api, facilityId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/facilities">← All facilities</BackToList>
        <div className="next-note next-note--error">
          Could not load this facility.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!facility || !form) return <div className="next-page next-muted">Loading…</div>;

  const set = (name, value) => setForm(previous => ({ ...previous, [name]: value }));

  const pristine = toForm(facility);
  const dirty = Object.keys(form).some(key => form[key] !== pristine[key]);

  const errors = {
    Name: form.Name.trim() ? null : 'Name is required.',
    // Required: the backend cannot save a facility without a position (it failed
    // with a 500), and the map and "courts nearby" need one.
    Latitude: coordinateError(form.Latitude, 90),
    Longitude: coordinateError(form.Longitude, 180),
    // Required by the backend since the admin hardening (400 otherwise).
    PlaceAddress: String(form.PlaceAddress ?? '').trim() ? null : 'A facility needs its address.',
    ProviderId: form.ProviderId ? null : 'A facility belongs to a provider.',
    AreaId: form.AreaId ? null : 'A facility belongs to an area.'
  };
  const invalid = Object.values(errors).some(Boolean);

  // ── Pictures ─────────────────────────────────────────────────────────────

  const pick = (file, ratio, field) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setCropping({
        src: reader.result,
        ratio,
        field,
        format: file.type || 'image/png',
        suffix: SUFFIX[field]
      });
    };
  };

  const cropped = async (image, suffix) => {
    const target = cropping;
    setCropping(null);
    if (!target) return;

    try {
      const response = await api.products.saveImage(image, suffix, target.format);
      if (!response?.success) {
        setNote({ tone: 'error', text: response?.message || 'The image was not saved.' });
        return;
      }
      // The saved key comes back from the server; the old page picks the field
      // by sniffing the key, which is the same thing done here without the sniff.
      set(target.field, response.data);
      setNote({ tone: 'info', text: 'Image saved. Remember to save the facility.' });
    } catch (error) {
      setNote({ tone: 'error', text: error?.message || 'The image was not saved.' });
    }
  };

  const generatePreview = async (fromField, toField, suffix) => {
    const source = form[fromField];
    if (!source) return;

    setBusy(true);
    try {
      const preview = await api.products.generatePreview(source);
      const response = await api.products.saveImage(preview, suffix, 'image/png');
      if (!response?.success) {
        setNote({ tone: 'error', text: response?.message || 'The preview was not generated.' });
        set(toField, '');
        return;
      }
      set(toField, response.data);
      // The list preview is also stored inline on the facility, as base64.
      if (toField === 'PlacePhotoListPreviewUrl') set('PicturePreview', preview);
      setNote({ tone: 'info', text: 'Preview generated. Remember to save the facility.' });
    } catch (error) {
      setNote({ tone: 'error', text: error?.message || 'The preview was not generated.' });
    } finally {
      setBusy(false);
    }
  };

  const save = async event => {
    event.preventDefault();
    if (invalid || !dirty || busy) return;

    setBusy(true);
    setNote(null);
    try {
      // The api module reads `Provider.value` / `Area.value` and nests the rest.
      await api.facilities.editFacilityData({
        ...form,
        Provider: { value: form.ProviderId, label: form.ProviderName },
        Area: { value: form.AreaId, label: form.AreaName },
        PlaceAddress: {
          String: form.PlaceAddress,
          Location: { Latitude: form.Latitude, Longitude: form.Longitude }
        },
        PlaceEmailAddress: { Email: form.PlaceEmailAddress },
        PlacePhoneNumber: { Number: form.PlacePhoneNumber }
      });
      await load();
      setNote({ tone: 'info', text: 'Facility saved.' });
    } catch (error) {
      setNote({
        tone: 'error',
        text: error?.response?.data?.ErrorMessage || error?.message || 'Unable to save the facility.'
      });
    } finally {
      setBusy(false);
    }
  };

  const field = (name, label, { hint, type = 'text' } = {}) => (
    <label className="next-filter" key={name}>
      <span className="next-filter__label">{label}</span>
      <input
        className="next-input"
        type={type}
        value={form[name]}
        onChange={event => set(name, event.target.value)}
      />
      {(errors[name] || hint) && (
        <span className={errors[name] ? 'next-form__error' : 'next-form__hint'}>
          {errors[name] || hint}
        </span>
      )}
    </label>
  );

  if (cropping) {
    // Full width while cropping: the old page does the same, and a cropper in a
    // column is a cropper you cannot see.
    return (
      <div className="next-page">
        <ImageCropper
          imageSrc={cropping.src}
          aspectRatio={cropping.ratio}
          format={cropping.format}
          pictureSize={SIZES[cropping.field]}
          link={cropping.suffix}
          onImageCropped={cropped}
          onCancel={() => setCropping(null)}
        />
      </div>
    );
  }

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/facilities">← All facilities</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{facility.Name || 'Facility'}</h1>
          {facility.Category && <span className="next-pill next-pill--neutral">{facility.Category}</span>}
        </div>
        <span className="next-detail__id">{facility.Id}</span>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <form onSubmit={save}>
            <section className="next-card">
              <div className="next-card__head"><h2>Venue</h2></div>
              <div className="next-form__grid">
                {field('Name', 'Name')}
                {field('Slug', 'Slug', { hint: 'Used in the public URL.' })}
                {field('PlaceAddress', 'Address')}
                {field('Latitude', 'Latitude')}
                {field('Longitude', 'Longitude')}
                {field('PlaceEmailAddress', 'Email', { type: 'email' })}
                {field('PlacePhoneNumber', 'Phone number')}
                {field('PlaceWebsiteUrl', 'Website')}
              </div>
            </section>

            <section className="next-card">
              <div className="next-card__head"><h2>Ownership</h2></div>
              <div className="next-form__grid">
                <LookupSelect
                  label="Provider"
                  value={form.ProviderId}
                  onChange={value => set('ProviderId', value)}
                  loadOptions={key => lookupProvider(api, key)}
                  placeholder="Name or id"
                />
                <LookupSelect
                  label="Area"
                  value={form.AreaId}
                  onChange={value => set('AreaId', value)}
                  loadOptions={key => api.areas.lookupByNameOrId(key)}
                  placeholder="Name or id"
                />
                <label className="next-filter">
                  <span className="next-filter__label">Locker</span>
                  <select
                    className="next-input"
                    value={form.LockerId}
                    onChange={event => set('LockerId', event.target.value)}
                  >
                    <option value="">None</option>
                    {lockers.map(locker => (
                      <option key={locker.Id || locker.value} value={locker.Id || locker.value}>
                        {locker.Name || locker.label || locker.Id}
                      </option>
                    ))}
                  </select>
                  {lockers.length === 0 && (
                    <span className="next-form__hint">No locker is registered at this facility.</span>
                  )}
                </label>
              </div>
              {(errors.ProviderId || errors.AreaId) && (
                <div className="next-form__grid">
                  <span className="next-form__error">{errors.ProviderId || errors.AreaId}</span>
                </div>
              )}
            </section>

            <section className="next-card">
              <div className="next-card__head">
                <h2>Pictures</h2>
                <span className="next-muted">each one is cropped to the ratio the site uses</span>
              </div>

              <div className="next-imageslots">
                <ImageSlot
                  label="Logo"
                  value={form.PlaceLogoUrl}
                  onChange={value => set('PlaceLogoUrl', value)}
                  onPick={(file, ratio) => pick(file, ratio, 'PlaceLogoUrl')}
                  ratio={RATIOS.logo}
                  hint="Square"
                  busy={busy}
                />

                <ImageSlot
                  label="Square photo"
                  value={form.PlacePhotoSquareUrl}
                  onChange={value => set('PlacePhotoSquareUrl', value)}
                  onPick={(file, ratio) => pick(file, ratio, 'PlacePhotoSquareUrl')}
                  ratio={RATIOS.square}
                  hint="Square"
                  previewLabel="Square preview"
                  previewValue={form.PlacePhotoSquarePreviewUrl}
                  onGeneratePreview={() => generatePreview('PlacePhotoSquareUrl', 'PlacePhotoSquarePreviewUrl', '-squared-preview')}
                  busy={busy}
                />

                <ImageSlot
                  label="List photo"
                  value={form.PlacePhotoListUrl}
                  onChange={value => set('PlacePhotoListUrl', value)}
                  onPick={(file, ratio) => pick(file, ratio, 'PlacePhotoListUrl')}
                  ratio={RATIOS.list}
                  hint="240 × 144"
                  previewLabel="List preview"
                  previewValue={form.PlacePhotoListPreviewUrl}
                  onGeneratePreview={() => generatePreview('PlacePhotoListUrl', 'PlacePhotoListPreviewUrl', '-list-preview')}
                  busy={busy}
                />

                <ImageSlot
                  label="Huge photo"
                  value={form.PlacePhotoHugeUrl}
                  onChange={value => set('PlacePhotoHugeUrl', value)}
                  onPick={(file, ratio) => pick(file, ratio, 'PlacePhotoHugeUrl')}
                  ratio={RATIOS.huge}
                  hint="1015 × 425"
                  previewLabel="Huge preview"
                  previewValue={form.PlacePhotoHugePreviewUrl}
                  onGeneratePreview={() => generatePreview('PlacePhotoHugeUrl', 'PlacePhotoHugePreviewUrl', '-huge-preview')}
                  busy={busy}
                />
              </div>
            </section>

            <div className="next-card">
              <div className="next-form__actions">
                <button
                  type="submit"
                  className="next-btn next-btn--primary"
                  disabled={invalid || !dirty || busy}
                  title={invalid ? 'Some fields need fixing first' : (!dirty ? 'Nothing has changed' : undefined)}
                >
                  {busy ? 'Saving…' : 'Save changes'}
                </button>
                <button
                  type="button"
                  className="next-btn"
                  disabled={!dirty || busy}
                  onClick={() => setForm(toForm(facility))}
                >
                  Discard
                </button>
                {dirty && <span className="next-muted">Unsaved changes</span>}
              </div>
            </div>

            <section className="next-card">
              <div className="next-card__head">
                <h2>Facility texts</h2>
                <Link className="next-btn next-btn--quiet" to={`/next/facility-text/new?facilityId=${facilityId}`}>
                  Add language
                </Link>
              </div>
              {facilityTexts.length === 0 ? (
                <p className="next-muted" style={{ padding: '0 20px 18px' }}>
                  No texts yet: the page uses the generated title, meta and heading.
                </p>
              ) : (
                <div className="next-table-wrap">
                  <table className="next-table">
                    <thead>
                      <tr>
                        <th>Language</th>
                        <th>SEO title</th>
                        <th>H1</th>
                        <th className="next-table__center">Meta</th>
                        <th className="next-table__center">Text</th>
                      </tr>
                    </thead>
                    <tbody>
                      {facilityTexts.map((text) => (
                        <tr key={text.id}>
                          <td>
                            <Link to={`/next/facility-text/${text.id}`}>
                              {text.languageName || languageLabel(text.language)}
                            </Link>
                          </td>
                          <td>{text.metaTitle || <span className="next-muted">—</span>}</td>
                          <td>{text.h1Title || <span className="next-muted">—</span>}</td>
                          <td className="next-table__center">
                            <span className={`next-pill next-pill--${text.metaDescription ? 'ok' : 'neutral'}`}>
                              {text.metaDescription ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="next-table__center">
                            <span className={`next-pill next-pill--${text.description ? 'ok' : 'neutral'}`}>
                              {text.description ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="next-card">
              <div className="next-card__head"><h2>Read only</h2></div>
              <div className="next-facts">
                <div className="next-fact">
                  <span className="next-fact__label">Id</span>
                  <span className="next-fact__value"><Guid value={facility.Id} /></span>
                </div>
                <div className="next-fact">
                  <span className="next-fact__label">Created at</span>
                  <span className="next-fact__value">{fmtDate(facility.CreatedAt)}</span>
                </div>
                <div className="next-fact">
                  <span className="next-fact__label">Type</span>
                  <span className="next-fact__value">{facility.Type || '—'}</span>
                </div>
              </div>
            </section>
          </form>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                form.ProviderId && {
                  label: 'Provider',
                  hint: form.ProviderName || undefined,
                  to: `/next/provider/${form.ProviderId}`
                },
                { label: 'Signups', hint: 'at this facility', to: `/next/signups?facilityId=${facilityId}` },
                { label: 'Seasonal courts', to: `/next/seasonal-courts?FacilityId=${facilityId}` },
                { label: 'Participants', to: `/next/participants?facilityId=${facilityId}` },
                { label: 'Products', href: `/products?facilityId=${facilityId}` },
                { label: 'Activities', href: `/activities?facilityId=${facilityId}` },
                { label: 'Redirects', hint: 'old slugs pointing here', to: `/next/facility-redirects?targetSlug=${encodeURIComponent(form.Slug || '')}` },
                form.LockerId && { label: 'Locker', href: `/locker/${form.LockerId}` }
              ]}
            />
          </section>
        </aside>
      </div>
    </div>
  );
};

// The keys the saved image is stored under, and the sizes the cropper renders at
// — both straight from the old page.
const SUFFIX = {
  PlaceLogoUrl: '-logo',
  PlacePhotoSquareUrl: '-squared',
  PlacePhotoListUrl: '-list',
  PlacePhotoHugeUrl: '-huge'
};

const SIZES = {
  PlaceLogoUrl: { width: 80, height: 80 },
  PlaceLogoUrlPreview: null,
  PlacePhotoSquareUrl: { width: 80, height: 80 },
  PlacePhotoListUrl: { width: 240, height: 144 },
  PlacePhotoHugeUrl: { width: 1015, height: 425 }
};

FacilityInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Facility = withApi(FacilityInternal);

Facility.requiresLogin = true;

export default Facility;
